/* @flow */

import clsx from 'clsx';
import React, { useImperativeHandle } from 'react';

import type { BasicFunction } from '../../helpers/jsHelpers/types';

import { logError } from '../../helpers/debug/debug';
import { Setting } from '../../helpers/settings/types';
import './Toggle.css';

export type ToggleHandle = {
  click: BasicFunction,
};

type TogglePropType = {
  +className?: string,
  +checked: boolean,
  +disabled?: boolean,
  +onChange: (checked: boolean, setting?: Setting) => Promise<void> | void,
  +setting?: Setting,
  +unfocused?: boolean,
};

const Toggle = ({ checked, className, disabled = false, onChange, setting, unfocused = false }: TogglePropType, ref: React.RefSetter<ToggleHandle>): React.Node => {
  const handleOnChange = React.useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      const {
        target: { checked },
      } = event;

      const result = onChange(checked, setting);

      if (result instanceof Promise) {
        result.catch((error) => {
          logError(`Error in Toggle onChange: ${error.message}`);
        });
      }
    },
    [onChange, setting],
  );

  const click = React.useCallback(() => {
    const result = onChange(!checked, setting);

    if (result instanceof Promise) {
      result.catch((error) => {
        logError(`Error in Toggle onChange: ${error.message}`);
      });
    }
  }, [checked, onChange, setting]);

  useImperativeHandle(ref, (): ToggleHandle => {
    return {
      click,
    };
  });

  return (
    <label className={clsx('toggleLabel', className, disabled && 'toggleDisabled', unfocused && 'toggleUnfocused')}>
      <input checked={checked} className='toggleInput' disabled={disabled} onChange={handleOnChange} tabIndex={-1} type='checkbox' />
      <span className='toggleSlider' />
    </label>
  );
};

export default React.forwardRef<TogglePropType, ToggleHandle>(Toggle) as React.ComponentType<{
  ...TogglePropType,
  +ref?: React.RefSetter<ToggleHandle> | { current: ToggleHandle | null, ... } | null,
}>;
